import type { EcommerceNewsItem } from '../mock/getMockNews';

import { NewsSources } from './NewsSources';
import { NewsSellerPoints } from './NewsSeleerPoints';
import { convertAsteriskToNormal, convertAsteriskToStrong, insertLineBreaks } from '@/utils/parser';
import parse, { domToReact } from 'html-react-parser';
import type { HTMLReactParserOptions } from 'html-react-parser';
import { NewsCharts } from './NewsCharts';
import { useRef, useEffect } from 'react';

export const parseSummary = (html: string) => {
  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (domNode.name === 'strong') {
        return <strong className="Body4B14">{domToReact(domNode.children)}</strong>;
      }
    },
  };

  return parse(html, options);
};

export const NewsCard = ({ item, active }: { item: EcommerceNewsItem; active: boolean }) => {
  const rawSummary = convertAsteriskToStrong(item.summary);
  const parsedSummary = parseSummary(rawSummary);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 카드가 활성화되었을 때 scrollTop = 0
  useEffect(() => {
    if (active && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [active]);

  return (
    <div
      ref={scrollRef}
      className="scrollbar flex h-full w-full flex-1 flex-col gap-[20px] overflow-y-auto rounded-[12px]  bg-white  p-[20px]  "
    >
      <div className="flex flex-col gap-[8px] text-gr-900">
        <NewsCardHeader title={item.title} />
        <NewsCardBody summary={parsedSummary} />
      </div>

      {Object.keys(item.keywordCount).length > 0 &&
        Object.values(item.keywordCount).some((arr) => Array.isArray(arr) && arr.length > 0) && (
          <NewsCharts keywordCount={item.keywordCount} active={active} />
        )}
      {item.sellerPoints && item.sellerPoints.length > 0 && (
        <NewsSellerPoints sellerPoints={item.sellerPoints} />
      )}
      {item.sources && item.sources.length > 0 && <NewsSources sources={item.sources} />}
    </div>
  );
};

export const NewsCardHeader = ({
  title,
  isDashboard = false,
}: Pick<EcommerceNewsItem, 'title'> & { isDashboard?: boolean }) => {
  const rawTitle = convertAsteriskToNormal(title);
  const lineBrokenTitle = insertLineBreaks(rawTitle);

  return (
    <h3 className="whitespace-pre-line break-keep Body1S16">
      {isDashboard ? rawTitle : lineBrokenTitle}
    </h3>
  );
};

export const NewsCardBody = ({ summary }: { summary: React.ReactNode }) => {
  return (
    <div className="whitespace-pre-wrap break-normal text-[14px] font-medium leading-[22px] text-gr-800">
      {summary}
    </div>
  );
};
